import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';

import ExternalCoachContext from '../../../../context/ExternalCoachContext';
import LoggedInUserContext from '../../../../../context/LoggedInUserContext';
import AnnouncementContext from '../../../../context/AnnouncementContext';
import { ANNOUNCEMENT_UNAVAILABLE_ERROR } from '../../../../context/AnnouncementContext/AnnouncementContextProvider';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useQuickSearch from '../../../../../hooks/useQuickSearch';
import useToast from '../../../../hooks/useToast';
import AnnouncementComment from '../../../../Model/AnnouncementComment';
import { ReportStatus } from '../../../../Model/AnnouncementCommentReport';
import LabelCheckbox from '../../../../components/LabelCheckbox';
import { PrimaryButton } from '../../../../../components/Button/ActionButtons';
import GenericDataGrid from '../../../../components/GenericDataGrid';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import QuickSearchToolbar from '../../../../components/QuickSearchToolbar';
import {
  HeaderRow,
  TitleContainer,
  Title,
} from '../../../../../components/v2/Header';

import FullPostView from '../FullPostView';
import { TabContainer } from '../../styles';

import {
  ActionButtonContainer,
  StyledResolveIcon,
  StyledTrashIcon,
  StyledViewIcon,
} from './styles';
import getColumns from './columns';
import texts from './texts.json';

const MAX_REASON_LENGTH_SHOW_IN_DIALOG = 70;
const MAX_COMMENT_LENGTH_SHOW_IN_DIALOG = 100;

const ACTIONS = {
  REMOVE_COMMENT: 'REMOVE_COMMENT',
  IGNORE_REPORT: 'IGNORE_REPORT',
};

const CommunityReports = () => {
  const [commentTextMap, setCommentTextMap] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [performingAction, setPerformingAction] = useState(null);

  const { externalCoachDoc: { id: coachId } } = useContext(ExternalCoachContext);
  const { userId } = useContext(LoggedInUserContext);
  const {
    isReady,
    updateCommentAndAnnouncementState,
    updateCommentAndAnnouncementStateById,
    selectedAnnouncement,
    commentReports,
    showResolvedReports,
    onShowResolvedReports,
    isLoading: isCommentReportsLoading,
  } = useContext(AnnouncementContext);
  const isComponentMountedRef = useComponentMounted();
  const { showToast } = useToast();

  useEffect(() => {
    const fetchUniqueComments = async () => {
      setIsLoading(true);
      const uniqueComments = new Map();
      commentReports.forEach((doc) => {
        if (!uniqueComments.has(doc.commentId)) {
          uniqueComments.set(doc.commentId, {
            commentId: doc.commentId,
            announcementId: doc.announcementId,
          });
        }
      });
      const commentsCollections = await AnnouncementComment.getCommentsForGivenIds(Array.from(uniqueComments.values()));
      if (isComponentMountedRef.current) {
        setIsLoading(false);
        setCommentTextMap(
          commentsCollections.reduce((acc, comment) => {
            acc[comment.id] = comment.comment;
            return acc;
          }, {}),
        );
      }
    };
    if (commentReports.length > 0) {
      fetchUniqueComments();
    }
  }, [
    coachId,
    isComponentMountedRef,
    showResolvedReports,
    commentReports,
  ]);

  const onClickAction = useCallback((action, report) => {
    setPerformingAction(action);
    setSelectedReport(report);
  }, []);

  const cancelConfirmDialog = useCallback(() => {
    setPerformingAction(null);
    setSelectedReport(null);
  }, []);

  const handleIgnoreReport = useCallback(async () => {
    setIsLoading(true);
    await selectedReport.updateStatus(ReportStatus.IGNORED, userId);
    if (isComponentMountedRef.current) {
      setSelectedReport(null);
      setIsLoading(false);
    }
    showToast(texts.reportIgnoredSuccessfully);
  }, [
    selectedReport,
    isComponentMountedRef,
    showToast,
    userId,
  ]);

  const handleRemoveComment = useCallback(async () => {
    setIsLoading(true);
    const commentDoc = await AnnouncementComment.getCommentById(
      selectedReport.commentId,
      selectedReport.announcementId,
    );
    // Remove comment here and let the server function handle resolving the all reports added to the same comment
    await commentDoc.remove(userId);

    if (isComponentMountedRef.current) {
      setSelectedReport(null);
      setIsLoading(false);
    }
    showToast(texts.commentRemovedSuccessfully);
  }, [
    selectedReport,
    isComponentMountedRef,
    userId,
    showToast,
  ]);

  const onClickViewComment = useCallback(async (report) => {
    try {
      await updateCommentAndAnnouncementStateById(report.announcementId, report.commentId);
    } catch (error) {
      if (error.code === ANNOUNCEMENT_UNAVAILABLE_ERROR) {
        // Post is gone — surface it and auto-resolve the now-unactionable report.
        showToast(texts.cannotViewComment, { warning: true });
        await report.updateStatus(ReportStatus.IGNORED, userId);
      } else {
        showToast(texts.viewCommentError, { error: true });
      }
    }
  }, [
    updateCommentAndAnnouncementStateById,
    showToast,
    userId,
  ]);

  const actionsRenderCell = useCallback(({ row }) => (
    <ActionButtonContainer>
      <PrimaryButton
        variant="info"
        size="medium"
        icon={<StyledViewIcon />}
        onClick={() => onClickViewComment(row)}
        disabled={row.status !== ReportStatus.OPEN}
      >
        {texts.viewComment}
      </PrimaryButton>
      <PrimaryButton
        variant="info"
        size="medium"
        icon={<StyledResolveIcon />}
        onClick={() => onClickAction(ACTIONS.IGNORE_REPORT, row)}
        disabled={row.status !== ReportStatus.OPEN}
      >
        {texts.ignore}
      </PrimaryButton>
      <PrimaryButton
        variant="info"
        size="medium"
        icon={<StyledTrashIcon />}
        onClick={() => onClickAction(ACTIONS.REMOVE_COMMENT, row)}
        disabled={row.status !== ReportStatus.OPEN}
      >
        {texts.removeComment}
      </PrimaryButton>
    </ActionButtonContainer>
  ), [onClickAction, onClickViewComment]);

  const columns = useMemo(() => getColumns({ actionsRenderCell, commentTextMap }), [actionsRenderCell, commentTextMap]);

  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(commentReports, columns);

  const dialogTexts = useMemo(() => {
    if (performingAction === ACTIONS.REMOVE_COMMENT) {
      return {
        title: texts.removeComment,
        content: format(texts.removeCommentDescription, {
          report: selectedReport?.reason?.length > MAX_REASON_LENGTH_SHOW_IN_DIALOG
            ? `${selectedReport?.reason?.slice(0, MAX_REASON_LENGTH_SHOW_IN_DIALOG)}...`
            : selectedReport?.reason,
          comment: commentTextMap[selectedReport?.commentId]?.length > MAX_COMMENT_LENGTH_SHOW_IN_DIALOG
            ? `${commentTextMap[selectedReport?.commentId]?.slice(0, MAX_COMMENT_LENGTH_SHOW_IN_DIALOG)}...`
            : commentTextMap[selectedReport?.commentId],
        }),
      };
    }
    return {
      title: texts.ignoreReport,
      content: format(texts.ignoreReportDescription, {
        report: selectedReport?.reason?.length > MAX_REASON_LENGTH_SHOW_IN_DIALOG
          ? `${selectedReport?.reason?.slice(0, MAX_REASON_LENGTH_SHOW_IN_DIALOG)}...`
          : selectedReport?.reason,
        comment: commentTextMap[selectedReport?.commentId]?.length > MAX_COMMENT_LENGTH_SHOW_IN_DIALOG
          ? `${commentTextMap[selectedReport?.commentId]?.slice(0, MAX_COMMENT_LENGTH_SHOW_IN_DIALOG)}...`
          : commentTextMap[selectedReport?.commentId],
      }),
    };
  }, [performingAction, selectedReport, commentTextMap]);

  return (
    <>
      <TabContainer>
        <HeaderRow>
          <TitleContainer>
            <Title>{texts.title}</Title>
          </TitleContainer>
        </HeaderRow>
        <GenericDataGrid
          rows={quickSearchRows}
          columns={columns}
          components={{
            Toolbar: QuickSearchToolbar,
          }}
          componentsProps={{
            toolbar: {
              ...toolbarProps,
              placeholder: texts.searchPlaceholder,
              filterTools: [
                {
                  Component: LabelCheckbox,
                  id: 'resolved-reports-filter',
                  props: {
                    isChecked: showResolvedReports,
                    description: texts.showResolvedReports,
                    onChange: (isChecked) => onShowResolvedReports(isChecked),
                  },
                },
              ],
            },
          }}
        />
        <LoadingOverlay isLoading={isLoading || isCommentReportsLoading || !isReady} />
        <ConfirmDialog
          isOpen={!!selectedReport}
          onConfirm={performingAction === ACTIONS.REMOVE_COMMENT ? handleRemoveComment : handleIgnoreReport}
          onCancel={cancelConfirmDialog}
          dialogTexts={dialogTexts}
        />
      </TabContainer>
      {/* Gated on the post, not on the comment id: switching the open post to another community's
          copy clears the deep link, and gating on it would close the dialog mid-read. */}
      {!!selectedAnnouncement && (
        <FullPostView
          isOpen={!!selectedAnnouncement}
          onClose={() => updateCommentAndAnnouncementState(null, null)}
        />
      )}
    </>
  );
};

export default compose(
  observer,
)(CommunityReports);
